import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import type {
  Components,
  Options as ReactMarkdownOptions,
} from "react-markdown";

import {
  markdownRehypePlugins,
  markdownRemarkPlugins,
} from "@/components/content/markdown-math";
import { cn } from "@/lib/utils";

export const ASSISTANT_RESPONSE_CHAT_CLASS =
  "typeset typeset-chat max-w-[720px] min-w-0 whitespace-normal [overflow-wrap:anywhere] [&_pre]:max-w-full [&_pre]:overflow-x-auto";
export const ASSISTANT_RESPONSE_DOCUMENT_CLASS =
  "typeset typeset-document typeset-measure w-full max-w-none min-w-0 whitespace-normal [overflow-wrap:anywhere] [&_pre]:max-w-full [&_pre]:overflow-x-auto";

/**
 * An assistant turn's Markdown as markup: the typeset wrapper, the shared
 * remark and rehype plugins and whatever renderer map the caller passes. It
 * holds no state, so it renders anywhere the plugins can run, including on
 * a server. AssistantResponseMarkdown supplies the live parts: streaming
 * plugins, evidence links and the answer document's narrative paragraphs.
 */
export function AssistantResponseMarkdownView({
  children,
  className,
  variant = "chat",
  streaming = false,
  components,
  remarkPlugins = markdownRemarkPlugins,
  rehypePlugins = markdownRehypePlugins,
  urlTransform,
}: {
  /** Markdown that has already been normalised for rendering. */
  children: string;
  className?: string;
  variant?: "chat" | "document";
  streaming?: boolean;
  components?: Partial<Components>;
  remarkPlugins?: NonNullable<ReactMarkdownOptions["remarkPlugins"]>;
  rehypePlugins?: NonNullable<ReactMarkdownOptions["rehypePlugins"]>;
  urlTransform?: ReactMarkdownOptions["urlTransform"];
}) {
  return (
    <div
      className={cn(
        variant === "document"
          ? ASSISTANT_RESPONSE_DOCUMENT_CLASS
          : ASSISTANT_RESPONSE_CHAT_CLASS,
        className,
      )}
      data-streaming={streaming ? "true" : undefined}
    >
      {children ? (
        <ReactMarkdown
          remarkPlugins={remarkPlugins}
          rehypePlugins={rehypePlugins}
          components={components}
          urlTransform={urlTransform ?? defaultUrlTransform}
        >
          {children}
        </ReactMarkdown>
      ) : null}
    </div>
  );
}
